import React from 'react'
import { Avatar, Tooltip } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { EnergyAvatar } from './energy-avatar'

interface IUserAvatar {
  id: string
  avatar?: string
  name: string
  avatarSize?: number | 'small' | 'default' | 'large'
  isShowingName?: boolean
  isLinkToPage?: boolean
  isTooltip?: boolean
  shape: 'square' | 'circle'
  style?: React.CSSProperties
  className?: string
  showEnergy?: boolean
  energyScore?: number
}

const legacyImageBaseUrl = process.env.REACT_APP_LEGACY_IMAGE_BASE_URL
const cdnImageBaseUrl = process.env.REACT_APP_CDN_IMAGE_BASE_URL

export const UserAvatar = ({ id, avatar, name, avatarSize, isShowingName, isLinkToPage, isTooltip, shape, style, className,
  showEnergy, energyScore }: IUserAvatar): React.ReactElement => {
  // Process avatar URL once for both components
  let avatarUrl = (legacyImageBaseUrl && cdnImageBaseUrl && avatar) ? avatar.replace(legacyImageBaseUrl, cdnImageBaseUrl) : avatar

  if (avatarUrl && !avatarUrl.includes('https')) {
    avatarUrl = `https://${avatarUrl}`
  }

  if (avatarUrl && !avatarUrl.includes(id) && avatarUrl.includes('cloudfront')) {
    avatarUrl = `${avatarUrl}/${id}`
  }

  // If showing energy, always use EnergyAvatar component (handles undefined energy scores)
  if (showEnergy) {
    let energyAvatarSize = 40
    let energyAvatarType: 'small' | 'default' | 'large' = 'default'
    if ((typeof avatarSize === 'number' && avatarSize >= 64) || avatarSize === 'large') {
      energyAvatarSize = 90
      energyAvatarType = 'large'
    }
    if ((typeof avatarSize === 'number' && avatarSize <= 32) || avatarSize === 'small') {
      energyAvatarSize = 32
      energyAvatarType = 'small'
    }
    let result = (
      <EnergyAvatar
        id={id}
        avatarUrl={avatarUrl}
        name={name}
        energyScore={energyScore} // Can be undefined, EnergyAvatar handles this
        size={energyAvatarSize}
        type={energyAvatarType}
      />
    )

    if (isShowingName) {
      result = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {result}
          <span className="name">{name}</span>
        </div>
      )
    }

    if (isLinkToPage) {
      result = <Link to={`/app/users/${id}`}>{result}</Link>
    }

    if (isTooltip && name) {
      result = <Tooltip title={name} key={id}>{result}</Tooltip>
    }

    return <div className={className}>{result}</div>
  }

  // Original avatar functionality
  const avatarData = <Avatar key={id} size={avatarSize} icon={<UserOutlined />} src={avatarUrl} shape={shape || 'circle'} style={style} />

  let result = avatarData

  if (isShowingName) {
    const nameData = <>{result} <span className="name">{name}</span></>
    result = nameData
  }

  if (isLinkToPage) {
    const linkData = <Link to={`/app/users/${id}`}>{result}</Link>
    result = linkData
  }

  if (isTooltip && name) {
    const tooltipData = <Tooltip title={name} key={id}>{result}</Tooltip>
    result = tooltipData
  }

  return <div className={className}>{result}</div>
}
